/**
 * "My Journal" page — Sprint 3 / FR-L-4.
 *
 * Lists every reflection the current user has written across all modules,
 * most-recent first. Reflections are candidate-private (FR-L-2), so this
 * page has no analog for admins — even the route is learner-only.
 *
 * Skipped reflections (body=null) still render as rows so the user can
 * see "you opened the Module 1 prompt but skipped it" and revisit
 * later (Sprint 4+ might add an "answer the skipped prompt" CTA).
 */
import * as React from "react";
import { useEffect, useState } from "react";

import { API_BASE_URL, ApiError } from "@/lib/api";
import { getMyJournal, type MyJournalEntry } from "@/lib/module-api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; entries: MyJournalEntry[] }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

export interface MyJournalPageProps {
  /** Test seam. */
  onUnauthenticated?: (returnTo: string) => void;
  fetchFn?: typeof getMyJournal;
}

export function MyJournalPage({
  onUnauthenticated = redirectToLogin,
  fetchFn = getMyJournal,
}: MyJournalPageProps = {}): React.JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const entries = await fetchFn();
        if (cancelled) return;
        setState({ kind: "ready", entries });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated("/journal");
          return;
        }
        const msg =
          err instanceof Error ? err.message : "Could not load your journal.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFn, onUnauthenticated]);

  if (state.kind === "loading") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6" aria-busy="true">
        <p className="text-sm text-muted-foreground" role="status">
          Loading your journal…
        </p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-bold text-foreground">My Journal</h1>
        <p className="mt-4 text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  const { entries } = state;
  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <header>
        <h1 className="text-3xl font-bold text-foreground">My Journal</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your private reflections from each module you've completed.
          Nothing here is graded, and no one else can read these entries.
        </p>
      </header>

      {entries.length === 0 ? (
        <p className="mt-12 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No reflections yet. Pass a module quiz, then write a reflection
          (or skip — you can always come back).
        </p>
      ) : (
        <ol className="mt-10 space-y-6">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="rounded-lg border border-border bg-white p-6"
            >
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Module {entry.module_sequence} · {entry.module_title}
                </p>
                <time
                  dateTime={entry.created_at}
                  className="text-xs text-muted-foreground"
                >
                  {new Date(entry.created_at).toLocaleDateString()}
                </time>
              </div>
              {entry.body === null ? (
                <p className="mt-3 text-sm italic text-muted-foreground">
                  (Skipped — no reflection written.)
                </p>
              ) : (
                <p className="mt-3 whitespace-pre-line text-sm text-foreground">
                  {entry.body}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
